import { TransitsApiType } from '../apis/TransitsApi';
import {
  DialogPlacesType,
  DialogTrainInfoType,
  DialogTrainsInfoType,
  IconOverviewType,
  IconZoomType,
  TransitsType
} from '../context/TransitsContextProvider';

// 系統ごとの車両アイコンの色を取得
export const getTrainColor = (course: number) => {
  switch (course) {
    case 1:
      return '#00b0f0';
    case 3:
      return '#FF0000';
    case 4:
      return '#ff0';
    case 5:
      return '#00b200';
    default:
      return '#BFBFBF';
  }
};

// 指定桁数で0埋め
const zeroPadding = (num: number, len: number) => {
  return (Array(len).join('0') + num).slice(-len);
};

// Date型を MM/DD hh:mm フォーマットに整形
export const formatDateTime = (_date: Date) => {
  const month = zeroPadding(_date.getMonth() + 1, 2);
  const date = zeroPadding(_date.getDate(), 2);
  const hour = zeroPadding(_date.getHours(), 2);
  const minute = zeroPadding(_date.getMinutes(), 2);

  return `${month}/${date} ${hour}:${minute}`;
};

// APIデータをContextの型に変換
export const translateTransitsApiResponse = (
  res: TransitsApiType,
  initialState: TransitsType
): TransitsType => {
  if (res === null) return initialState;

  const _icons_overview: Array<IconOverviewType> = [];
  const _icons_overview_low_floor: Array<IconOverviewType> = [];
  const _icons_zoom: Array<IconZoomType> = [];
  const _icons_zoom_low_floor: Array<IconZoomType> = [];
  const _dialog_places: DialogPlacesType = {};
  const _dialog_places_low_floor: DialogPlacesType = {};

  for (const _place in res.datas) {
    const place = res.datas[_place];
    let _index_in_place = 0; //拡大図：全車両における同一のplace内でのインデックス
    let _index_in_place_low_floor = 0; //拡大図：低床車フィルタにおける同一のplace内でのインデックス

    const _dialog_place_trains: Array<DialogTrainInfoType> = [];
    const _dialog_place_trains_low_floor: Array<DialogTrainInfoType> = [];

    const trains = place.trains;
    for (const train of trains) {
      // 全体図：全車両
      if (train.x_overview !== null && train.y_overview !== null) {
        const icon_overview: IconOverviewType = {
          x: train.x_overview,
          y: train.y_overview,
          train_no: train.train_no,
          color: getTrainColor(train.course),
          arrow: place.arrow,
          logo: train.low_floor_flg,
          onStation: train.station_name_former === train.station_name_latter
        };
        _icons_overview.push(icon_overview);
      }

      // 全体図：低床車フィルタ
      if (
        train.x_overview_low_floor !== null &&
        train.y_overview_low_floor !== null &&
        train.low_floor_flg === true
      ) {
        const icon_overview_low_floor: IconOverviewType = {
          x: train.x_overview_low_floor,
          y: train.y_overview_low_floor,
          train_no: train.train_no,
          color: getTrainColor(train.course),
          arrow: place.arrow,
          logo: train.low_floor_flg,
          onStation: train.station_name_former === train.station_name_latter
        };
        _icons_overview_low_floor.push(icon_overview_low_floor);
      }

      // 拡大図：全車両
      if (train.x_zoom !== null && train.y_zoom !== null) {
        const icon_zoom: IconZoomType = {
          x: train.x_zoom,
          y: train.y_zoom,
          train_no: train.train_no,
          color: getTrainColor(train.course),
          arrow: place.arrow,
          logo: train.low_floor_flg,
          place_id: train.place_id,
          index_in_place: _index_in_place++
        };
        _icons_zoom.push(icon_zoom);
      }

      // 拡大図：低床車フィルタ
      if (
        train.x_zoom_low_floor !== null &&
        train.y_zoom_low_floor !== null &&
        train.low_floor_flg === true
      ) {
        const icon_zoom_low_floor: IconZoomType = {
          x: train.x_zoom_low_floor,
          y: train.y_zoom_low_floor,
          train_no: train.train_no,
          color: getTrainColor(train.course),
          arrow: place.arrow,
          logo: train.low_floor_flg,
          place_id: train.place_id,
          index_in_place: _index_in_place_low_floor++
        };
        _icons_zoom_low_floor.push(icon_zoom_low_floor);
      }

      // ダイアログ：全車両
      const _dialog_train_info: DialogTrainInfoType = {
        self_station_no: train.station_no,
        station_name_former: train.station_name_former,
        station_name_latter: train.station_name_latter,
        station_name_former_en: train.station_name_former_en,
        station_name_latter_en: train.station_name_latter_en,
        train_no: train.train_no,
        course: train.course,
        low_floor_flag: train.low_floor_flg,
        destination_name: train.destination_name_ja,
        destination_name_en: train.destination_name_en,
        schedules: train.arriving_estimate
      };
      _dialog_place_trains.push(_dialog_train_info);

      // ダイアログ：低床車フィルタ
      if (train.low_floor_flg === true) {
        const _dialog_train_info_low_floor: DialogTrainInfoType = {
          self_station_no: train.station_no,
          station_name_former: train.station_name_former,
          station_name_latter: train.station_name_latter,
          station_name_former_en: train.station_name_former_en,
          station_name_latter_en: train.station_name_latter_en,
          train_no: train.train_no,
          course: train.course,
          low_floor_flag: train.low_floor_flg,
          destination_name: train.destination_name_ja,
          destination_name_en: train.destination_name_en,
          schedules: train.arriving_estimate
        };
        _dialog_place_trains_low_floor.push(_dialog_train_info_low_floor);
      }
    }

    const _dialog_trains_info: DialogTrainsInfoType = {
      trains: _dialog_place_trains.slice(0, 4) //最大4台まで
    };
    _dialog_places[_place] = _dialog_trains_info;

    const _dialog_trains_info_low_floor: DialogTrainsInfoType = {
      trains: _dialog_place_trains_low_floor.slice(0, 4) //最大4台まで
    };
    _dialog_places_low_floor[_place] = _dialog_trains_info_low_floor;
  }

  return {
    icons_overview: _icons_overview,
    icons_overview_low_floor: _icons_overview_low_floor,
    icons_zoom: _icons_zoom,
    icons_zoom_low_floor: _icons_zoom_low_floor,
    dialog_places: _dialog_places,
    dialog_places_low_floor: _dialog_places_low_floor
  };
};
